// src/components/admin/ErrorTicketsTab.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "notistack";
import { useTranslation } from "../../i18n";
import { fetchData, updateData, deleteData, createData } from "../../api/apiHelpers";
import { athenaDarkPaperSx, athenaBackdropSx, athenaDarkTitleSx, athenaDarkContentSx } from "../layout/AthenaDialogStyles";
import { glass, glassTableSx } from "../../theme/glassTokens";

interface TicketSummary {
  id: string;
  created_at: string;
  severity: string;
  status: string;
  title: string;
  source: string;
  exception_type: string;
  occurrence_count: number;
  last_seen_at: string | null;
  affected_user_email: string | null;
  request_path: string;
}

interface TicketDetail extends TicketSummary {
  updated_at: string;
  description: string;
  fingerprint: string;
  affected_user: { id: number; email: string } | null;
  affected_patient_id: number | null;
  task_id: string;
  resolved_at: string | null;
  resolved_by: string;
  resolution_notes: string;
}

interface TicketListResponse {
  results: TicketSummary[];
  count: number;
  num_pages: number;
  page: number;
}

const SEVERITY_COLORS: Record<string, "error" | "warning" | "info" | "default"> = {
  critical: "error",
  high: "warning",
  medium: "info",
  low: "default",
};

const STATUS_COLORS: Record<string, "error" | "warning" | "success" | "default"> = {
  open: "error",
  investigating: "warning",
  resolved: "success",
  wont_fix: "default",
};

const ErrorTicketsTab: React.FC = () => {
  const theme = useTheme();
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<TicketListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/billing/admin/error-tickets/?page=${page}&page_size=20`;
      if (severityFilter) url += `&severity=${severityFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const resp = await fetchData<TicketListResponse>(url);
      setData(resp);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [page, severityFilter, statusFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const openDetail = async (ticketId: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const resp = await fetchData<TicketDetail>(`/billing/admin/error-tickets/${ticketId}/`);
      setDetail(resp);
      setNewStatus(resp.status);
      setResolutionNotes(resp.resolution_notes || "");
    } catch {
      enqueueSnackbar(t("error_tickets.load_error", "Failed to load ticket"), { variant: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!detail) return;
    try {
      const resp = await updateData<{ status: string; resolution_notes: string }, TicketDetail>(
        `/billing/admin/error-tickets/${detail.id}/`,
        { status: newStatus, resolution_notes: resolutionNotes },
      );
      setDetail(resp);
      enqueueSnackbar(t("error_tickets.update_success", "Ticket updated"), { variant: "success" });
      loadTickets();
    } catch {
      enqueueSnackbar(t("error_tickets.update_error", "Failed to update ticket"), { variant: "error" });
    }
  };

  const handleDeleteTicket = async (ticketId: string) => {
    try {
      await deleteData(`/billing/admin/error-tickets/${ticketId}/delete/`);
      enqueueSnackbar(t("error_tickets.delete_success", "Ticket deleted"), { variant: "success" });
      setDetail(null);
      loadTickets();
    } catch {
      enqueueSnackbar(t("error_tickets.delete_error", "Failed to delete ticket"), { variant: "error" });
    }
  };

  const handleClearResolved = async () => {
    try {
      const resp = await createData<Record<string, never>, { deleted: number }>(
        "/billing/admin/error-tickets/clear-resolved/",
        {},
      );
      enqueueSnackbar(`${resp.deleted} ${t("error_tickets.clear_success", "resolved tickets cleared")}`, { variant: "success" });
      loadTickets();
    } catch {
      enqueueSnackbar(t("error_tickets.clear_error", "Failed to clear tickets"), { variant: "error" });
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("error_tickets.severity_label", "Severity")}</InputLabel>
          <Select value={severityFilter} label={t("error_tickets.severity_label", "Severity")} onChange={(e) => { setSeverityFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">{t("error_tickets.filter_all", "All")}</MenuItem>
            {["critical", "high", "medium", "low"].map(s => (
              <MenuItem key={s} value={s}>{t(`error_tickets.severity_${s}`, s)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("error_tickets.status_label", "Status")}</InputLabel>
          <Select value={statusFilter} label={t("error_tickets.status_label", "Status")} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">{t("error_tickets.filter_all", "All")}</MenuItem>
            {["open", "investigating", "resolved", "wont_fix"].map(s => (
              <MenuItem key={s} value={s}>{t(`error_tickets.status_${s}`, s.replace("_", " "))}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={handleClearResolved}
        >
          {t("error_tickets.clear_resolved", "Clear Resolved")}
        </Button>
      </Box>

      {/* Ticket table */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small" sx={glassTableSx}>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", color: glass.textSecondary } }}>
                <TableCell>{t("error_tickets.col_time", "Time")}</TableCell>
                <TableCell>{t("error_tickets.col_severity", "Severity")}</TableCell>
                <TableCell>{t("error_tickets.col_status", "Status")}</TableCell>
                <TableCell>{t("error_tickets.col_title", "Title")}</TableCell>
                <TableCell>{t("error_tickets.col_source", "Source")}</TableCell>
                <TableCell align="right">{t("error_tickets.col_count", "#")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : data?.results.map((ticket) => (
                    <TableRow
                      key={ticket.id}
                      hover
                      sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}
                      onClick={() => openDetail(ticket.id)}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                        {formatDate(ticket.created_at)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={ticket.severity} color={SEVERITY_COLORS[ticket.severity] || "default"} sx={{ fontWeight: 600, fontSize: "0.7rem" }} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={ticket.status.replace("_", " ")} color={STATUS_COLORS[ticket.status] || "default"} variant="outlined" sx={{ fontSize: "0.7rem" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem", maxWidth: 350, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ticket.title}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.8rem" }}>{ticket.source}</TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                        {ticket.occurrence_count}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.num_pages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={data.num_pages} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Box>

      {data && (
        <Typography variant="caption" sx={{ color: textSecondary, mt: 1, display: "block" }}>
          {data.count} {t("error_tickets.total_tickets", "total tickets")}
        </Typography>
      )}

      {/* Ticket Detail Dialog */}
      <Dialog
        open={detail !== null || detailLoading}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={{ ...athenaDarkTitleSx, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={700}>
            {t("admin.ticket_detail", "Error Ticket")}
          </Typography>
          <IconButton size="small" onClick={() => setDetail(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx} dividers>
          {detailLoading ? (
            <Box sx={{ py: 3 }}>
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={200} />
            </Box>
          ) : detail ? (
            <Box>
              {/* Meta */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: textSecondary }}>{t("error_tickets.detail_severity", "Severity")}</Typography>
                  <Box><Chip size="small" label={detail.severity} color={SEVERITY_COLORS[detail.severity] || "default"} /></Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: textSecondary }}>{t("error_tickets.detail_status", "Status")}</Typography>
                  <Box><Chip size="small" label={detail.status.replace("_", " ")} color={STATUS_COLORS[detail.status] || "default"} /></Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: textSecondary }}>{t("error_tickets.detail_source", "Source")}</Typography>
                  <Typography variant="body2">{detail.source}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: textSecondary }}>{t("error_tickets.detail_occurrences", "Occurrences")}</Typography>
                  <Typography variant="body2" fontWeight={600}>{detail.occurrence_count}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{detail.title}</Typography>

              {detail.exception_type && (
                <Chip size="small" label={detail.exception_type} variant="outlined" sx={{ mb: 1, fontFamily: "monospace" }} />
              )}
              {detail.affected_user && (
                <Typography variant="body2" sx={{ color: textSecondary, mb: 0.5 }}>
                  {t("error_tickets.affected_user", "Affected user:")} {detail.affected_user.email}
                </Typography>
              )}
              {detail.request_path && (
                <Typography variant="body2" sx={{ color: textSecondary, mb: 0.5 }}>
                  {t("error_tickets.request_path", "Path:")} {detail.request_path}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Traceback */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {t("error_tickets.description_traceback", "Description / Traceback")}
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: alpha(theme.palette.common.black, 0.04),
                  fontFamily: "'Fira Code', Consolas, monospace",
                  fontSize: "0.75rem",
                  lineHeight: 1.6,
                  maxHeight: 300,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {detail.description}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Resolution form */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {t("error_tickets.resolution", "Resolution")}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 180, mb: 2 }}>
                <InputLabel>{t("error_tickets.status_label", "Status")}</InputLabel>
                <Select value={newStatus} label={t("error_tickets.status_label", "Status")} onChange={(e) => setNewStatus(e.target.value)}>
                  {["open", "investigating", "resolved", "wont_fix"].map(s => (
                    <MenuItem key={s} value={s}>{t(`error_tickets.status_${s}`, s.replace("_", " "))}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                size="small"
                label={t("error_tickets.resolution_notes", "Resolution Notes")}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                sx={{ mb: 2 }}
              />

              {detail.resolved_at && (
                <Typography variant="caption" sx={{ color: textSecondary }}>
                  {t("error_tickets.resolved_by", "Resolved by")} {detail.resolved_by} {t("error_tickets.resolved_on", "on")} {new Date(detail.resolved_at!).toLocaleString()}
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => detail && handleDeleteTicket(detail.id)}
            disabled={!detail}
          >
            {t("error_tickets.delete_button", "Delete")}
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setDetail(null)}>{t("error_tickets.close_button", "Close")}</Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleResolve}
              disabled={!detail}
            >
              {t("error_tickets.update_button", "Update")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErrorTicketsTab;
